import type {
  FailureReason,
  FiatFlow,
  SubmitTransition,
} from '@funkit/connect-core'
import type { TFunction } from 'i18next'

import { logger } from '../logging/logger'

import type { FiatOtpScreenProps } from './FiatOtpScreen'
import type { KnownContacts } from './knownContacts'
import { contactOnChannel } from './knownContacts'

/**
 * Binds a `SESSION_AUTH` step response to the code screen.
 *
 * The input's name and length are read off the transition, not assumed: the
 * contract declares them per step, and a step that words them differently
 * still renders.
 *
 * `undefined` means the step is unanswerable — no CTA, or one that collects
 * nothing.
 */
export function otpScreenProps(
  flow: FiatFlow,
  t: TFunction,
  /** Every contact the entry collected, so each round can name its own. */
  contacts: KnownContacts,
): Omit<FiatOtpScreenProps, 'onClose'> | undefined {
  const state = flow.stepResponse?.state
  if (state?.kind !== 'SESSION_AUTH') {
    return undefined
  }
  // A provider-drawn round has no code to collect: the screen would take the
  // transition's only text input — the phone — and post the code under it.
  if (state.channel === 'PROVIDER_RENDERED') {
    return undefined
  }

  const cta = flow.ctas.find((transition) => transition.id !== RESEND_ID)
  if (cta === undefined) {
    return undefined
  }

  const input = codeInput(cta)
  if (input === undefined) {
    return undefined
  }

  // Every frame draws the resend, so the row is always there; whether it is
  // live is the round's to say. A provider that carries no transition for it
  // gets a disabled link, not a press that does nothing.
  const resend = flow.ctas.find((transition) => transition.id === RESEND_ID)

  // The step names the channel it dispatched on, so the copy follows the
  // server rather than what the client happened to collect.
  const isSms = state.channel === 'SMS_OTP'

  return {
    // The one for this round's channel: a session that verifies a number and
    // then an address collected both, and naming the number under the email
    // round is a lie.
    destination: contactOnChannel(contacts, isSms),
    isSms,
    length: input.length,
    errorMessage: otpErrorMessage(state.error, resend !== undefined, t),
    onResend: resend === undefined ? undefined : () => void flow.submit(resend),
    isSubmitting: flow.isInFlight,
    onChangeCode: (code) => flow.setField(input.name, code),
    onSubmit: () => void flow.submit(cta),
  }
}

/**
 * The id a provider's resend carries, whichever shape it takes: a new code here,
 * or a freshly minted surface on the rails that render their own auth.
 */
export const RESEND_ID = 'resend'

/** The step's one text input — its name, and the digits it expects. */
function codeInput(
  cta: SubmitTransition,
): { name: string; length?: number } | undefined {
  const entry = Object.entries(cta.inputs ?? {}).find(
    ([, spec]) => spec.type === 'TEXT',
  )
  if (entry === undefined) {
    return undefined
  }
  const [name, spec] = entry
  return { name, length: spec.type === 'TEXT' ? spec.length : undefined }
}

function otpErrorMessage(
  error: FailureReason | undefined,
  canResend: boolean,
  t: TFunction,
): string | undefined {
  if (error === undefined) {
    return undefined
  }

  logger.info('connect-rn:fiat:otp_rejected', { code: error.code })
  if (error.category !== 'USER_ERROR') {
    return t('fiat.submitFailed')
  }
  if (error.code === 'OTP_DISPATCH_THROTTLED') {
    return t('fiat.otpRateLimited')
  }
  if (error.code === 'CODE_EXPIRED' && canResend) {
    return t('fiat.otpExpired')
  }
  return t('fiat.otpError')
}
